import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@shared/lib/cn'
import { Button, ButtonSize, ButtonVariant } from '../Button'

/**
 * Светлое затемнение с лёгким размытием вместо плоского dim.
 *
 * Для окон поверх насыщенной страницы, где важно, чтобы фон остался читаемым:
 * гейт выбора языка и финал прогона в задачах участника. Живёт здесь, а не у
 * них, потому что это решение о виде диалога, а не о конкретном экране.
 */
export const DIALOG_SOFT_OVERLAY = 'bg-foreground/16 backdrop-blur-[1px]'

/**
 * Ширина иллюстрированного окна участника — 456px в макете. Гейт выбора
 * языка и финал прогона задач нарисованы одним окном, и ширина у них общая.
 */
export const DIALOG_ILLUSTRATED_WIDTH = 'max-w-[456px]'

export interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  description?: string
  children: React.ReactNode
  className?: string
  /** Описание окна для скринридера задано в `children` (через `DialogPrimitive.Description`):
   *  запасное описание-копия заголовка не подставляется. */
  hasOwnDescription?: boolean
  /** Название окна для скринридера задано в `children` (через `DialogPrimitive.Title`):
   *  запасное «Диалоговое окно» не подставляется. */
  hasOwnTitle?: boolean
  /** Перекрывает затемнение. Экраны поверх насыщенной страницы берут более
   *  светлый тон с размытием вместо плоского dim по умолчанию. */
  overlayClassName?: string
  /** Дополняет классы кнопки закрытия. Нужен окну без шапки на тёмном содержимом
   *  (плеер): крестик по умолчанию тёмный и на нём не виден. */
  closeClassName?: string
  /** When provided, the portal renders into this element and the popup is
   *  positioned relative to it (anchored mode: no overlay, no built-in close button). */
  container?: HTMLElement | null
  /** Фокус при открытии окна: вызывается до того, как Radix переносит его внутрь. */
  onOpenAutoFocus?: (event: Event) => void
  /** Фокус при закрытии окна; `preventDefault` отключает возврат Radix. */
  onCloseAutoFocus?: (event: Event) => void
  /** `false` — окно закрывается только своими кнопками: без крестика, Esc и клик
   *  мимо не закрывают. Для обязательных уведомлений. */
  dismissible?: boolean
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  hasOwnDescription = false,
  hasOwnTitle = false,
  overlayClassName,
  closeClassName,
  container,
  onOpenAutoFocus,
  onCloseAutoFocus,
  dismissible = true,
}: DialogProps) {
  const { t } = useTranslation('common')
  const isAnchored = container != null

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal container={container}>
        <DialogPrimitive.Overlay
          className={cn(
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-40 bg-black/50',
            overlayClassName
          )}
        />

        <DialogPrimitive.Content
          onOpenAutoFocus={onOpenAutoFocus}
          onCloseAutoFocus={onCloseAutoFocus}
          onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
          onInteractOutside={dismissible ? undefined : (e) => e.preventDefault()}
          className={cn(
            isAnchored
              ? 'absolute top-full right-0 z-50 mt-2'
              : // Ширина считается от экрана за вычетом полей: на телефоне окно с
                // `w-full` вставало впритык к краям, как шторка без отступов.
                'fixed top-1/2 left-1/2 z-50 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2',
            // Паддинг 20px — из макета (394:120657, 394:121930).
            'bg-surface shadow-modal rounded-2xl p-5',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            className
          )}
        >
          {(title || description) && (
            // Шапка отделена линией: 16px под текстом, затем граница, и общий
            // отступ до содержимого (макет 394:120657). Крестик стоит в той же
            // строке, что и заголовок, поэтому справа оставлено место под него.
            <div className={cn('border-border mb-5 border-b pb-4', dismissible && 'pr-10')}>
              {title ? (
                <DialogPrimitive.Title className="typo-heading text-heading">
                  {title}
                </DialogPrimitive.Title>
              ) : null}
              {description ? (
                <DialogPrimitive.Description className="typo-base text-secondary mt-1">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
          )}
          {!title && !hasOwnTitle && (
            <DialogPrimitive.Title className="sr-only">
              {t('dialog.title_fallback')}
            </DialogPrimitive.Title>
          )}
          {!description && !hasOwnDescription && (
            // Radix предупреждает в консоли о диалоге без описания, а свой
            // `id` на описании разрывает связь, которую он проставляет сам, —
            // поэтому здесь только запасной текст, без `id` и без ручного
            // `aria-describedby` на содержимом.
            <DialogPrimitive.Description className="sr-only">
              {title ?? ''}
            </DialogPrimitive.Description>
          )}

          {children}

          {dismissible && (
            <DialogPrimitive.Close asChild>
              <Button
                onClick={() => onOpenChange(false)}
                size={ButtonSize.sm}
                aria-label={t('dialog.close')}
                variant={ButtonVariant.ghost}
                // Крестик 20×20 на одной строке с заголовком: `top/right-4` плюс
                // собственный `p-1` дают ровно 20px отступа от края попапа, а
                // padding остаётся кликабельной зоной.
                //
                // Цвет — `text/black` (#222222) из макета (722:30031). Раньше
                // стоял `text-secondary`: он был снят пиксельно со сглаженной
                // иконки, а Figma теперь отдаёт стиль прямо.
                className={cn(
                  'text-foreground hover:bg-border hover:text-heading focus:ring-orange absolute top-4 right-4 rounded-lg p-1 transition-colors focus:ring-2 focus:outline-none',
                  closeClassName
                )}
              >
                <X className="size-5" />
              </Button>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
